import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M25 L25.06 — AI/ML governance overview.
 *
 * Composed read-only dashboard over existing endpoints: the model registry
 * (`GET /v1/ai/models`) for lifecycle/approval/critical posture, and the AI
 * audit trail (`GET /v1/ai/audit-logs`) for the most recent governance events.
 */
interface Model {
  readonly modelId: string;
  readonly modelCode: string;
  readonly modelName: string;
  readonly modelType: string;
  readonly status: string;
  readonly approvalStatus?: string | null;
  readonly isCritical?: boolean;
}

interface AuditEvent {
  readonly auditId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly action: string;
  readonly actorId: string | null;
  readonly overrideReason: string | null;
  readonly eventTimestamp: string;
}

function countBy(rows: Model[], key: (m: Model) => string): [string, number][] {
  const map = new Map<string, number>();
  for (const r of rows) {
    const k = key(r) || 'UNKNOWN';
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

function Stat({ label, value, testId }: { label: string; value: ReactNode; testId: string }): ReactNode {
  return (
    <div data-testid={testId} style={{ border: '1px solid var(--color-border-default)', borderRadius: 8, padding: '12px 16px', minWidth: 120 }}>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</div>
    </div>
  );
}

export function MlGovernancePage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [models, setModels] = useState<Model[] | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setError(null);
      try {
        const [m, a] = await Promise.all([
          apiRequest<{ data: Model[]; total?: number }>('/v1/ai/models?limit=200', { token }),
          apiRequest<{ data: AuditEvent[]; total?: number }>('/v1/ai/audit-logs?limit=10', { token }),
        ]);
        if (!active) return;
        setModels(m.data);
        setEvents(a.data);
      } catch (e) {
        if (active) setError((e as Error).message);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const critical = models?.filter((m) => m.isCritical).length ?? 0;
  const approved = models?.filter((m) => (m.approvalStatus ?? '').toUpperCase() === 'APPROVED').length ?? 0;
  const pendingApproval = models?.filter((m) => (m.approvalStatus ?? '').toUpperCase() === 'PENDING').length ?? 0;

  return (
    <main aria-label="ml-governance">
      <h1 style={{ fontSize: 20, margin: '0 0 16px' }}>{t('m25.governance.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!models ? (
        !error && <p>{t('m25.governance.loading')}</p>
      ) : (
        <>
          <section aria-label="governance-stats" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <Stat testId="stat-models" label={t('m25.governance.statModels')} value={models.length} />
            <Stat testId="stat-critical" label={t('m25.governance.statCritical')} value={critical} />
            <Stat testId="stat-approved" label={t('m25.governance.statApproved')} value={approved} />
            <Stat testId="stat-pending" label={t('m25.governance.statPending')} value={pendingApproval} />
          </section>

          <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>{t('m25.governance.byStatus')}</h2>
          <table aria-label="status-breakdown" style={{ borderCollapse: 'collapse', minWidth: 260 }}>
            <tbody>
              {countBy(models, (m) => m.status).map(([status, n]) => (
                <tr key={status} data-testid={`status-${status}`} style={{ borderBottom: '1px solid var(--color-bg-muted)' }}>
                  <td style={{ padding: 8 }}>{status}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{n}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>{t('m25.governance.recentActivity')}</h2>
          {events.length === 0 ? (
            <p data-testid="events-empty">{t('m25.governance.eventsEmpty')}</p>
          ) : (
            <table aria-label="events-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--color-border-default)' }}>
                  <th style={{ padding: 8 }}>{t('m25.governance.colEvent')}</th>
                  <th style={{ padding: 8 }}>{t('m25.governance.colEntity')}</th>
                  <th style={{ padding: 8 }}>{t('m25.governance.colAction')}</th>
                  <th style={{ padding: 8 }}>{t('m25.governance.colWhen')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.auditId} data-testid={`event-${e.auditId}`} style={{ borderBottom: '1px solid var(--color-bg-muted)' }}>
                    <td style={{ padding: 8 }}>{e.eventType}</td>
                    <td style={{ padding: 8 }}>{e.entityType}</td>
                    <td style={{ padding: 8 }}>{e.action}</td>
                    <td style={{ padding: 8 }}>{at(e.eventTimestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
